import { Platform } from 'react-native';
import {
  getRemoteConfig as getRemoteConfigModule,
  getValue as getRemoteConfigValue,
  setDefaults as setRemoteConfigDefaults,
  setConfigSettings as setRemoteConfigSettings,
} from '@react-native-firebase/remote-config';

const CACHE_EXPIRY = 60 * 60 * 1000;
const {
  INSIDER_SMART_RECOMMENDATION_STATUS,
  INSIDER_SMART_RECOMMENDATION_DATA,
} = require('~config/default.env').default;

const DEFAULTS = {
  [INSIDER_SMART_RECOMMENDATION_STATUS]: false,
  [INSIDER_SMART_RECOMMENDATION_DATA]: '[]',
};

const DEFAULT_RESPONSE = { configStatus: false, configData: '[]' };

let cachedConfig = null;
let lastFetchTime = null;
let remoteConfigInstance = null;

/*
 * Fetches remote config values and caches them for an hour.
 * If the cache is not expired, it returns the cached values.
 * If the fetch fails, it returns the cached values or the default values.
 * @returns {Object} - { configStatus: boolean, configData: string }
 * @throws {Error} - If remote config fetch fails and no cached values are available.
 */
export const getRemoteConfig = async () => {
  // @react-native-firebase requires native initialization; not supported on web
  if (Platform.OS === 'web') {
    return DEFAULT_RESPONSE;
  }

  const now = Date.now();

  if (cachedConfig && lastFetchTime && now - lastFetchTime < CACHE_EXPIRY) {
    return cachedConfig;
  }

  if (!remoteConfigInstance) {
    remoteConfigInstance = getRemoteConfigModule();
  }

  try {
    await setRemoteConfigDefaults(remoteConfigInstance, DEFAULTS);

    await setRemoteConfigSettings(remoteConfigInstance, {
      minimumFetchIntervalMillis: 0,
      fetchTimeMillis: 60000,
    });

    let configStatus = false;
    try {
      configStatus = getRemoteConfigValue(
        remoteConfigInstance,
        INSIDER_SMART_RECOMMENDATION_STATUS,
      ).asBoolean();
      console.debug(
        `🕹️ --- \x1b[35m[LILLA-APPS]\x1b[0m[FIREBASE][Remote Config]: Status Fetched \x1b[35m${configStatus}\x1b[0m --- 🕹️`,
      );
    } catch (valueError) {
      console.debug(
        `🕹️ --- \x1b[35m[LILLA-APPS]\x1b[0m[FIREBASE][Remote Config]: Error retrieving config status \x1b[35m${valueError}\x1b[0m --- 🕹️`,
      );
    }

    let configData = '[]';
    try {
      configData = getRemoteConfigValue(
        remoteConfigInstance,
        INSIDER_SMART_RECOMMENDATION_DATA,
      ).asString();
      console.debug(
        `🕹️ --- \x1b[35m[LILLA-APPS]\x1b[0m[FIREBASE][Remote Config]: Data Fetched \x1b[35m${configData.length}\x1b[0m --- 🕹️`,
      );
    } catch (valueError) {
      console.debug(
        `🕹️ --- \x1b[35m[LILLA-APPS]\x1b[0m[FIREBASE][Remote Config]: Error retrieving config data \x1b[35m${valueError}\x1b[0m --- 🕹️`,
      );
    }

    cachedConfig = { configStatus, configData };
    lastFetchTime = now;

    return cachedConfig;
  } catch (error) {
    console.error('Remote config fetch failed:', error);
    console.error('Error stack:', error.stack);
    console.error('Error details:', JSON.stringify(error, null, 2));
    return cachedConfig || DEFAULT_RESPONSE;
  }
};

/*
 * Clears the remote config cache.
 */
export const clearRemoteConfigCache = () => {
  cachedConfig = null;
  lastFetchTime = null;
};
